import { portalText } from "../i18n/portal.js";
/**
 * The sidebar's three groups, as data.
 *
 * The prototype's `navGroups` array, ported one row at a time: Overview, Channels, Account.
 * Rows whose screen is not yet routed carry `to: null` — they render, they are marked as not
 * yet available, and they do not navigate anywhere. That is honest in a way that hiding them is
 * not: the customer can see what the product is, and the console never offers a link that goes
 * nowhere.
 *
 * AMENDED 2026-09-23 (fidelity wave). Every row is routed now — Contact lists, Compliance, Do not
 * call and Documentation have screens — and the rows carry the prototype's two remaining
 * behaviours:
 *
 *   · `activeFor` — the prototype marks a row current for a FAMILY of screens (Agents is current
 *     on an agent, a campaign, survey results and an order; Workspaces on a workspace; Compliance
 *     on the audit log), not only on its own path;
 *   · `count` — the tabular figure beside a row (Agents 2, Contact lists 3, Numbers & SIP 2,
 *     Workspaces 3, Do not call 41) is a real count the shell reads, never a literal. A count the
 *     gateway cannot give (an operation not built yet) is simply not drawn.
 *
 * "Live activity" opened the Live tab of the account's first VoiceAgent, as the prototype's does
 * (superseded 2026-09-29, below).
 *
 * AMENDED 2026-09-25 (customer-portal review #53, #88, #129, #135):
 *
 *   · exactly ONE row is current per location (`currentEntryLabel`), so an agent page no longer
 *     highlights Agents and Live activity together;
 *   · a row carries `readableBy`, and `navigationFor(role)` leaves out a page the role's reading
 *     route refuses (a Viewer no longer sees Contact lists, Do not call, Compliance or Test console
 *     only to be told "could not be read");
 *   · Numbers & SIP counts distinct held numbers, not binding rows (`heldNumberCount`);
 *   · a count still loading is drawn as a placeholder of the same width, so the rows do not jump.
 *
 * AMENDED 2026-09-29 (plan-007 Stage 3 live checks): "Live activity" opens `/live`, the account-wide
 * page listing every conversation in flight on every VoiceAgent, instead of the first VoiceAgent's
 * Live tab. The product owner called their second agent and the page they were sent to stayed
 * empty. The row is an ordinary route now, so the `opens` mechanism and the "first agent" special
 * case of `currentEntryLabel` are gone; an agent's own Live tab is Agents' row, like every tab.
 *
 * AMENDED 2026-10-05 (plan-014 A4, FR12-SCR-05, design §27.15.1 "App shell"): navigation is derived
 * from capabilities, in the new export's four groups — Overview, Channels, **Organization**
 * (Organization → `/organization`, Finance → `/finance/funding`) and Account.
 *
 *   · a WORKSPACE row (every row of Overview, Channels and Account) is shown only with a workspace
 *     open, and then by the member's role as before (`readableBy`);
 *   · an ORGANIZATION row is shown by the person's organization role — `accountCapability`, read from
 *     the auth_session's capability listing — never by a workspace role (design §27.5.2: an account
 *     role never grants workspace content, and a workspace role never grants the organization);
 *   · with the organization alone open (account-only, Finance's normal state) only the Organization
 *     group is drawn: no operational navigation at all (FR12-AUTH-10, the prototype's
 *     `land.bella = ['org','funding']`).
 *
 * A4 is this file's single writer (plan-014 §2.3): a later page unit adds no line here — its page is
 * reached through the Organization or Finance row and its own tabs.
 *
 * AMENDED 2026-10-06 (plan-014 INT for C4b): with the organization alone open, an organization role that lists
 * `account.audit.financial_read` (Owner, Admin, Finance) also sees **Audit log** (`/audit`, the organization's log —
 * Finance its financial entries only). The row is `accountOnly`: with a workspace open the audit log is reached
 * through Compliance as before, so the row is not drawn and `/audit` stays Compliance's.
 *
 * AMENDED 2026-10-09 (design sync, Claude Design version 1791495439483263): the export's `navGroups`.
 *
 *   · Account loses Workspaces, Billing, Payouts and Do not call; Channels loses Test console;
 *   · Workspaces now live on Organization → Workspaces (`/organization/workspaces`, the prototype's
 *     `org:workspaces`, where its old `team` screen now goes), so Organization is current on a workspace's
 *     own page too; Billing and Payouts are Finance's "This workspace" and "Payouts" tabs, so Finance is
 *     current on them; Do not call is reached from Compliance ("View the list"), so Compliance is current
 *     there;
 *   · "New" pills (Live activity, Messaging) and the Numbers & SIP count are gone; Agents and Contact lists
 *     keep their counts;
 *   · a workspace member with no organization role still reaches the workspace's billing from the person
 *     menu ("Billing & credit"), where its Finance strip shows "This workspace" (and "Payouts" to the roles
 *     that read them) — the Organization group stays an organization role's alone (plan-014 A4).
 */
import type { AccountCapability } from "@wetalk/domain";
import type { PhosphorIcon } from "@wetalk/ui";

import { GLYPH } from "./glyph.js";
import { memberCan, type ConsoleAct } from "./member-capability.js";
import type { NumberPageView, WorkspaceRoleView } from "../api/gateway.js";

/** The figures the shell reads for the sidebar's count badges. */
export type NavigationCountKey = "voiceAgent" | "contactList";

export interface NavigationEntry {
  readonly label: string;
  readonly glyph: PhosphorIcon;
  /** The route this entry opens; `null` when the screen is not routed yet. */
  readonly to: string | null;
  /** The phase that will route it — shown as the reason it is not available. */
  readonly phase: string | null;
  readonly badge: string | null;
  /** `new` renders the accent pill the design uses; `count` the tabular figure. */
  readonly badgeKind: "new" | "count" | null;
  /** Path prefixes (besides `to`) on which this row is the current one. */
  readonly activeFor?: readonly string[];
  /** Paths under an `activeFor` prefix that are NOT this row (e.g. `/agents/new`). */
  readonly inactiveFor?: readonly string[];
  /** Which count the badge shows; the figure comes from the gateway, never from here. */
  readonly count?: NavigationCountKey;
  /**
   * The act a role must be able to do for this row to be shown at all. A page whose reading route
   * refuses the role is left out of the sidebar rather than offered and then refused with "could
   * not be read", which read like an outage (customer-portal review 2026-09-25 #135).
   */
  readonly readableBy?: ConsoleAct;
  /**
   * plan-014 A4: an ORGANIZATION row — shown only to a person whose organization role holds this
   * capability (the auth_session's listing), with or without a workspace open. Absent on every
   * workspace row, which needs a workspace open.
   */
  readonly accountCapability?: AccountCapability;
  /** plan-014 INT for C4b: an organization row drawn only with the organization ALONE open (no workspace). */
  readonly accountOnly?: true;
}

export interface NavigationGroup {
  readonly label: string;
  readonly entry: readonly NavigationEntry[];
}

export const NAVIGATION: readonly NavigationGroup[] = [
  {
    get label() { return portalText("shell", "overview_0efc2e6b"); },
    get entry(): readonly NavigationEntry[] { return [
      { get label() { return portalText("shell", "dashboard_d87f47b4"); }, glyph: GLYPH.squaresFour, to: "/dashboard", phase: null, badge: null, badgeKind: null },
      {
        get label() { return portalText("shell", "agents_64acf7e2"); },
        glyph: GLYPH.headset,
        to: "/agents",
        phase: null,
        badge: null,
        badgeKind: "count",
        count: "voiceAgent",
        activeFor: ["/agents/", "/campaigns/", "/orders/"],
        // New campaign is where a contact list is put to use (review 2026-09-25, CL #134).
        inactiveFor: ["/agents/new", "/campaigns/new"],
      },
      { get label() { return portalText("shell", "live_activity_97935e2e"); }, glyph: GLYPH.broadcast, to: "/live", phase: null, badge: null, badgeKind: null },
    ]; },
  },
  {
    get label() { return portalText("shell", "channels_18e03e2a"); },
    get entry(): readonly NavigationEntry[] { return [
      {
        get label() { return portalText("shell", "contact_lists_b2cf5d26"); },
        glyph: GLYPH.addressBook,
        to: "/contact-lists",
        phase: null,
        badge: null,
        badgeKind: "count",
        count: "contactList",
        readableBy: "contact_list.read",
        // One list's page (CL #121) and New campaign (CL #134) are Contact lists' too.
        activeFor: ["/contact-lists/", "/campaigns/new"],
      },
      { get label() { return portalText("shell", "numbers_sip_dad16cb4"); }, glyph: GLYPH.phoneCall, to: "/numbers", phase: null, badge: null, badgeKind: null },
      { get label() { return portalText("shell", "messaging_caef62a0"); }, glyph: GLYPH.chatText, to: "/messaging", phase: null, badge: null, badgeKind: null, activeFor: ["/messaging/"] },
    ]; },
  },
  {
    get label() { return portalText("shell", "organization_519255ae"); },
    get entry(): readonly NavigationEntry[] { return [
      {
        get label() { return portalText("shell", "organization_519255ae"); },
        glyph: GLYPH.buildings,
        to: "/organization",
        phase: null,
        badge: null,
        badgeKind: null,
        // A workspace's own page (and the retired `/workspaces`) belong to Organization → Workspaces.
        activeFor: ["/organization/", "/workspaces"],
        accountCapability: "account.profile.read",
      },
      {
        get label() { return portalText("shell", "finance_1b48d3f0"); },
        glyph: GLYPH.bank,
        to: "/finance/funding",
        phase: null,
        badge: null,
        badgeKind: null,
        // "This workspace" (Billing, its transactions) and "Payouts" are Finance tabs.
        activeFor: ["/finance/", "/billing", "/payouts"],
        accountCapability: "account.finance.read",
      },
      {
        get label() { return portalText("shell", "audit_log_3cfc5f1c"); },
        glyph: GLYPH.clockCounterClockwise,
        to: "/audit",
        phase: null,
        badge: null,
        badgeKind: null,
        accountCapability: "account.audit.financial_read",
        accountOnly: true,
      },
    ]; },
  },
  {
    get label() { return portalText("shell", "account_85dfa32c"); },
    get entry(): readonly NavigationEntry[] { return [
      { get label() { return portalText("shell", "settings_c7f73bb5"); }, glyph: GLYPH.slidersHorizontal, to: "/settings", phase: null, badge: null, badgeKind: null },
      {
        get label() { return portalText("shell", "compliance_68f0ae49"); },
        glyph: GLYPH.shieldCheck,
        to: "/compliance",
        phase: null,
        badge: null,
        badgeKind: null,
        // The audit log and the do-not-call list open from Compliance.
        activeFor: ["/audit", "/do-not-call"],
        readableBy: "compliance.read",
      },
      { get label() { return portalText("shell", "documentation_9e9cf322"); }, glyph: GLYPH.bookOpenText, to: "/docs", phase: null, badge: null, badgeKind: null, activeFor: ["/docs/"] },
    ]; },
  },
];

/** Whether `entry` matches `pathname` by its own path or its `activeFor` family. */
function matchesPath(entry: NavigationEntry, pathname: string): boolean {
  if (entry.to !== null && pathname === entry.to) return true;
  if ((entry.inactiveFor ?? []).some((prefix) => pathname.startsWith(prefix))) return false;
  return (entry.activeFor ?? []).some((prefix) => pathname.startsWith(prefix));
}

/**
 * The ONE current row for a location — its label, or `null` when no row is current.
 *
 * Customer-portal review 2026-09-25 #129: exactly one row is current per location. Since
 * 2026-09-29 "Live activity" is its own page (`/live`), so every agent page — its Live tab
 * included — is Agents'.
 */
export function currentEntryLabel(pathname: string, shown?: readonly NavigationGroup[]): string | null {
  // The rows actually drawn, when the sidebar says which; otherwise every row a workspace context draws (an
  // `accountOnly` row is never drawn beside a workspace, so `/audit` stays Compliance's there).
  const groups = shown === undefined ? NAVIGATION.map((group) => ({ ...group, entry: group.entry.filter((entry) => entry.accountOnly !== true) })) : shown;
  for (const group of groups) {
    for (const entry of group.entry) {
      if (matchesPath(entry, pathname)) return entry.label;
    }
  }
  return null;
}

/** What the sidebar is drawn for: the workspace role (none with the organization alone open) and the organization's capabilities. */
export interface NavigationScope {
  /** The member's role in the open workspace, or `null` when no workspace is open. */
  readonly memberRole: WorkspaceRoleView | null;
  /** What the person's organization role holds there (empty for workspace-only access). */
  readonly accountCapability: readonly AccountCapability[];
}

/** Whether one row is shown for `scope` (see the module note). */
function entryShown(entry: NavigationEntry, scope: NavigationScope): boolean {
  if (entry.accountOnly === true && scope.memberRole !== null) return false;
  if (entry.accountCapability !== undefined) return scope.accountCapability.includes(entry.accountCapability);
  if (scope.memberRole === null) return false;
  return entry.readableBy === undefined || memberCan(scope.memberRole, entry.readableBy);
}

/**
 * The sidebar as this person sees it in this scope: rows whose page they cannot open are left out
 * (#135), and an empty group is not drawn.
 */
export function navigationFor(given: NavigationScope | WorkspaceRoleView): readonly NavigationGroup[] {
  // A bare role is a workspace member with no organization role — how the rules were asked before
  // plan-014, and still how a page's own suite asks which workspace rows a role sees.
  const scope: NavigationScope = typeof given === "string" ? { memberRole: given, accountCapability: [] } : given;
  return NAVIGATION.map((group) => ({
    label: group.label,
    entry: group.entry.filter((entry) => entryShown(entry, scope)),
  })).filter((group) => group.entry.length > 0);
}

/**
 * The numbers the account holds: distinct numbers, released ones left out.
 *
 * Customer-portal review 2026-09-25 #88: the badge counted ROWS, and a number moved between
 * agents has two (the old binding is kept, disabled), so one number read "2".
 */
export function heldNumberCount(page: NumberPageView): number {
  return new Set(page.row.filter((one) => one.releasedAtText === null).map((one) => one.phoneNumberId)).size;
}
